/* eslint-disable unicorn/no-null -- Usage parsing returns `null` when payloads are incompatible by design. */

import { toNumberOrUndefined } from '@/lib/utils/type-coercion';
import { isRecord } from '@/lib/utils/type-guards';

export interface UsageMetrics {
  completionTokens?: number;
  promptTokens?: number;
  reasoningTokens?: number;
  totalTokens?: number;
}

export interface NormalizedUsageMetrics {
  completionTokens: number;
  promptTokens: number;
  reasoningTokens?: number;
  totalTokens: number;
}

const FIELD_MAP: Record<string, keyof UsageMetrics> = {
  promptTokens: 'promptTokens',
  prompt_tokens: 'promptTokens',
  inputTokens: 'promptTokens',
  input_tokens: 'promptTokens',
  completionTokens: 'completionTokens',
  completion_tokens: 'completionTokens',
  outputTokens: 'completionTokens',
  output_tokens: 'completionTokens',
  totalTokens: 'totalTokens',
  total_tokens: 'totalTokens',
  reasoningTokens: 'reasoningTokens',
  reasoning_tokens: 'reasoningTokens',
  thinkingTokens: 'reasoningTokens',
  thinking_tokens: 'reasoningTokens',
};

const toNumber = (value: unknown): number | undefined => toNumberOrUndefined(value);

export function parseUsageMetrics(payload: unknown): UsageMetrics | null {
  if (!isRecord(payload)) {
    return null;
  }
  const metrics: UsageMetrics = {};
  for (const [rawKey, targetKey] of Object.entries(FIELD_MAP)) {
    if (!(rawKey in payload)) {
      continue;
    }
    const value = toNumber(payload[rawKey]);
    if (value === undefined) {
      continue;
    }
    metrics[targetKey] = value;
  }
  return hasUsageMetrics(metrics) ? metrics : null;
}

export function mergeUsageMetrics(base: UsageMetrics, update: UsageMetrics): UsageMetrics {
  const next = { ...base };
  if (update.promptTokens !== undefined) {
    next.promptTokens = update.promptTokens;
  }
  if (update.completionTokens !== undefined) {
    next.completionTokens = update.completionTokens;
  }
  if (update.totalTokens !== undefined) {
    next.totalTokens = update.totalTokens;
  }
  if (update.reasoningTokens !== undefined) {
    next.reasoningTokens = update.reasoningTokens;
  }
  return next;
}

export function hasUsageMetrics(metrics: UsageMetrics | null | undefined): metrics is UsageMetrics {
  if (!metrics) {
    return false;
  }
  return (
    (metrics.promptTokens ?? 0) > 0 ||
    (metrics.completionTokens ?? 0) > 0 ||
    (metrics.totalTokens ?? 0) > 0 ||
    (metrics.reasoningTokens ?? 0) > 0
  );
}

export function normalizeUsageMetrics(metrics: UsageMetrics): NormalizedUsageMetrics {
  const promptTokens = Math.max(0, Math.trunc(metrics.promptTokens ?? 0));
  const completionTokens = Math.max(0, Math.trunc(metrics.completionTokens ?? 0));
  const reasoningTokens =
    metrics.reasoningTokens === undefined ? undefined : Math.max(0, Math.trunc(metrics.reasoningTokens));
  const totalTokens = Math.max(
    0,
    metrics.totalTokens === undefined
      ? promptTokens + completionTokens + Math.max(0, reasoningTokens ?? 0)
      : Math.trunc(metrics.totalTokens)
  );
  return {
    promptTokens,
    completionTokens,
    totalTokens,
    ...(reasoningTokens === undefined ? {} : { reasoningTokens }),
  };
}
